import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Pencil, RefreshCw, Send, UserMinus } from 'lucide-react';
import type { RemoveFromWaitlistRequest, WaitlistEntry } from '@vagusx/clients';
import { WAITLIST_REMOVAL_REASONS } from '@vagusx/clients';
import {
  useAddToWaitlist,
  useInvalidateWaitlistLists,
  usePushWaitlistEntry,
  useRefreshWaitlistEntry,
  useRemoveFromWaitlist,
  useUpdateWaitlistEntry,
  useWaitlistEntry,
} from '@/hooks/use-waitlist';
import { formatDate, formatDateTime } from '@/lib/format';
import { canResync, waitlistEhrSyncState } from '@/lib/ehr-sync-state';
import { DetailRow, DetailSection } from '@/components/custom/detail-section';
import { SheetActions, SheetBody, SheetCancel } from '@/components/custom/sheet-layout';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { WaitlistEntryForm } from './WaitlistEntryForm';
import { WaitlistEhrSyncStatus } from './WaitlistEhrSyncStatus';
import { addPayload, updatePayload, type WaitlistFormValues } from './waitlist-payload';
import {
  ENTRY_REFRESH_NOTICE,
  PRIORITY_LABEL,
  REMOVAL_REASON_LABEL,
  STATUS_LABEL,
  canCheckInEhr,
  canSendToEhr,
  formatDays,
  formatTimeRange,
  isOpenStatus,
  pushNotice,
  pushProgress,
  type EhrActionNotice,
} from './waitlist-format';

/** Give up watching a push someone asked for after this long; it still runs. */
const PUSH_WATCH_MS = 60_000;

function notify({ kind, message }: EhrActionNotice): void {
  toast[kind](message);
}

// =============================================================================
// Add
// =============================================================================

export function AddToWaitlistSheet({
  onClose,
  onAdded,
}: {
  onClose: () => void;
  /** The new entry's id: the page opens it, so its trip to the EHR can be followed. */
  onAdded: (id: string) => void;
}): React.ReactElement {
  const add = useAddToWaitlist();

  // A refusal propagates to the form, which shows it under its field.
  const handleSubmit = async (values: WaitlistFormValues): Promise<void> => {
    const { id } = await add.mutateAsync(addPayload(values));
    toast.success('Added to the waitlist');
    onAdded(id);
  };

  return (
    <Sheet open={true} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex w-full max-w-lg flex-col gap-0 sm:max-w-xl">
        <SheetHeader className="px-6">
          <SheetTitle>Add to waitlist</SheetTitle>
          <SheetDescription>
            Records what the patient wants; nothing is booked. The entry is sent to the EHR's
            waitlist too.
          </SheetDescription>
        </SheetHeader>
        <WaitlistEntryForm
          entry={null}
          submitLabel="Add to waitlist"
          onSubmit={handleSubmit}
          onCancel={onClose}
        />
      </SheetContent>
    </Sheet>
  );
}

// =============================================================================
// View / edit / remove
// =============================================================================

export function WaitlistEntrySheet({
  entryId,
  canEdit,
  onClose,
}: {
  entryId: string;
  canEdit: boolean;
  onClose: () => void;
}): React.ReactElement {
  // Set while a "Send to EHR now" is under way: the push runs in the
  // background, so the entry is re-read until it settles.
  const [isSending, setIsSending] = useState(false);
  const { data: entry, isLoading } = useWaitlistEntry(entryId, { pollWhileQueued: isSending });
  const update = useUpdateWaitlistEntry();
  const push = usePushWaitlistEntry();
  const check = useRefreshWaitlistEntry();
  const invalidateLists = useInvalidateWaitlistLists();
  const [isEditing, setIsEditing] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const editable = canEdit && !!entry && isOpenStatus(entry.status);

  useEffect(() => {
    if (!isSending || !entry) return;
    const progress = pushProgress(entry);
    if (progress === 'sending') return;
    setIsSending(false);
    void invalidateLists();
    notify(pushNotice(progress, entry, formatDateTime));
  }, [isSending, entry, invalidateLists]);

  useEffect(() => {
    if (!isSending) return;
    const timer = setTimeout(() => {
      setIsSending(false);
      toast.info('Still sending to the EHR. The entry shows when it lands.');
    }, PUSH_WATCH_MS);
    return () => clearTimeout(timer);
  }, [isSending]);

  const handlePush = async (): Promise<void> => {
    if (!entry) return;
    try {
      await push.mutateAsync(entry.id);
      setIsSending(true);
    } catch {
      // global mutation error handler shows the toast
    }
  };

  const handleCheck = async (): Promise<void> => {
    if (!entry) return;
    try {
      const { outcome } = await check.mutateAsync(entry.id);
      notify(ENTRY_REFRESH_NOTICE[outcome]);
    } catch {
      // global mutation error handler shows the toast
    }
  };

  // A failed or retrying entry is re-sent from Re-sync beside its status
  // (ADR-285); Send to EHR now covers one that is queued and hasn't gone yet.
  // Both send the same request.
  const showSendNow = !!entry && canSendToEhr(entry) && !canResync(waitlistEhrSyncState(entry.ehr));
  const ehrActions =
    canEdit && entry && (showSendNow || canCheckInEhr(entry)) ? (
      <div className="flex gap-2">
        {canCheckInEhr(entry) && (
          <Button size="sm" variant="outline" onClick={handleCheck} disabled={check.isPending}>
            <RefreshCw
              className={check.isPending ? 'mr-1.5 h-3.5 w-3.5 animate-spin' : 'mr-1.5 h-3.5 w-3.5'}
            />
            Check in EHR
          </Button>
        )}
        {showSendNow && (
          <Button
            size="sm"
            variant="outline"
            onClick={handlePush}
            disabled={push.isPending || isSending}
          >
            <Send className="mr-1.5 h-3.5 w-3.5" />
            {isSending ? 'Sending…' : 'Send to EHR now'}
          </Button>
        )}
      </div>
    ) : null;

  // A refusal propagates to the form, which shows it under its field.
  const handleSave = async (values: WaitlistFormValues): Promise<void> => {
    if (!entry) return;
    await update.mutateAsync({ id: entry.id, data: updatePayload(entry, values) });
    toast.success('Waitlist entry updated');
    setIsEditing(false);
  };

  return (
    <Sheet open={true} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex w-full max-w-lg flex-col gap-0 sm:max-w-xl">
        <SheetHeader className="px-6">
          <SheetTitle>
            {entry?.patient.name ?? (isLoading ? 'Loading…' : 'Waitlist entry')}
          </SheetTitle>
          <SheetDescription>
            {entry
              ? `${STATUS_LABEL[entry.status]} · on the list since ${formatDate(entry.createdAt)}`
              : ''}
          </SheetDescription>
        </SheetHeader>

        {entry && isEditing && (
          <WaitlistEntryForm
            entry={entry}
            submitLabel="Save changes"
            onSubmit={handleSave}
            onCancel={() => setIsEditing(false)}
          />
        )}

        {entry && !isEditing && (
          <>
            <SheetBody className="px-6 pb-6">
              <EntryDetails
                entry={entry}
                ehrActions={ehrActions}
                sending={isSending}
                onResync={canEdit ? handlePush : undefined}
                isResyncing={push.isPending || isSending}
              />
            </SheetBody>
            {editable && (
              <SheetActions
                className="px-6"
                start={
                  <Button variant="outline" onClick={() => setIsRemoving(true)}>
                    <UserMinus className="mr-1.5 h-3.5 w-3.5" />
                    Remove
                  </Button>
                }
              >
                <SheetCancel />
                <Button onClick={() => setIsEditing(true)}>
                  <Pencil className="mr-1.5 h-3.5 w-3.5" />
                  Edit
                </Button>
              </SheetActions>
            )}
          </>
        )}

        {entry && isRemoving && <RemoveDialog entry={entry} onDone={() => setIsRemoving(false)} />}
      </SheetContent>
    </Sheet>
  );
}

function EntryDetails({
  entry,
  ehrActions,
  sending,
  onResync,
  isResyncing,
}: {
  entry: WaitlistEntry;
  ehrActions: React.ReactNode;
  /** A Send to EHR now is under way: the only time a queued entry animates. */
  sending: boolean;
  onResync?: () => void;
  isResyncing: boolean;
}): React.ReactElement {
  return (
    <div className="space-y-2">
      <DetailSection title="Patient">
        <DetailRow label="Name" value={entry.patient.name} />
        <DetailRow label="Date of birth" value={formatDate(entry.patient.dateOfBirth)} />
      </DetailSection>

      <DetailSection title="Wants">
        <DetailRow
          label="Current appointment"
          value={
            entry.appointment
              ? [
                  formatDateTime(entry.appointment.startTime),
                  entry.appointment.providerName,
                  entry.appointment.locationName,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : 'None'
          }
        />
        <DetailRow label="Appointment type" value={entry.appointmentType?.name ?? 'Not set'} />
        <DetailRow label="Provider" value={entry.provider?.name ?? 'Any provider'} />
        <DetailRow label="Location" value={entry.location?.name ?? 'Any location'} />
        <DetailRow label="Days" value={formatDays(entry.preferences.preferredDaysOfWeek)} />
        <DetailRow
          label="Start time"
          value={formatTimeRange(entry.preferences.earliestTime, entry.preferences.latestTime)}
        />
        <DetailRow label="Priority" value={PRIORITY_LABEL[entry.priority]} />
        <DetailRow label="Notes" value={entry.notes} />
      </DetailSection>

      <DetailSection title="EHR" action={ehrActions}>
        <DetailRow
          label="Sync"
          value={
            entry.ehr.syncStatus === 'not_applicable' ? (
              'Not sent to an EHR'
            ) : (
              <WaitlistEhrSyncStatus
                entry={entry}
                sending={sending}
                onResync={onResync}
                isResyncing={isResyncing}
              />
            )
          }
        />
        <DetailRow label="EHR waitlist id" value={entry.ehr.waitlistId} />
        <DetailRow label="Next attempt" value={formatDateTime(entry.ehr.nextAttemptAt)} />
      </DetailSection>

      {entry.contact.attempts > 0 && (
        <DetailSection title="Contact">
          <DetailRow label="Attempts" value={String(entry.contact.attempts)} />
          <DetailRow label="Last contacted" value={formatDateTime(entry.contact.lastContactedAt)} />
        </DetailSection>
      )}
    </div>
  );
}

function RemoveDialog({
  entry,
  onDone,
}: {
  entry: WaitlistEntry;
  onDone: () => void;
}): React.ReactElement {
  const remove = useRemoveFromWaitlist();
  const [reason, setReason] = useState<RemoveFromWaitlistRequest['reason']>('no_longer_needed');

  const handleRemove = async (): Promise<void> => {
    try {
      await remove.mutateAsync({ id: entry.id, data: { reason } });
      toast.success('Removed from the waitlist');
      onDone();
    } catch {
      // global mutation error handler shows the toast
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onDone()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove {entry.patient.name} from the waitlist?</DialogTitle>
          <DialogDescription>
            Nothing is booked or cancelled. The entry closes here and is deleted from the EHR's
            waitlist.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label>Reason</Label>
          <Select
            value={reason}
            onValueChange={(v) => setReason(v as RemoveFromWaitlistRequest['reason'])}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WAITLIST_REMOVAL_REASONS.map((r) => (
                <SelectItem key={r} value={r}>
                  {REMOVAL_REASON_LABEL[r]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onDone}>
            Keep on the list
          </Button>
          <Button variant="destructive" onClick={handleRemove} disabled={remove.isPending}>
            Remove
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
